import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  lookbookApi,
  type Lookbook,
  type LookbookSettingsInput,
} from "@/api/lookbook-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getErrorMessage } from "@/lib/api-error";

const PASSWORD_MIN_LENGTH = 4;

/**
 * Who can get into one lookbook and what they can do there: an optional
 * password on top of the link, and whether signed-out visitors may upvote and
 * comment. Owners edit; members see the current state, since the endpoint
 * behind it is manager-only.
 */
export function LookbookAccessSettings({
  lookbook,
  workspaceId,
  canEdit,
  onChanged,
}: {
  lookbook: Lookbook;
  workspaceId: string | undefined;
  canEdit: boolean;
  onChanged: () => void;
}) {
  // null: not editing. A string: the password being typed.
  const [draft, setDraft] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (settings: LookbookSettingsInput) => {
      if (!workspaceId) throw new Error("No workspace");
      return lookbookApi.updateSettings(workspaceId, lookbook._id, settings);
    },
    onSuccess: (_lookbook, settings) => {
      setDraft(null);
      onChanged();
      if (settings.password === null) {
        toast.success("Password removed — the link alone opens it again");
      } else if (typeof settings.password === "string") {
        toast.success(
          lookbook.password_protected
            ? "Password changed — visitors need the new one"
            : "Password set — share it with the people who should get in"
        );
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update lookbook access"));
    },
  });

  const savePassword = () => {
    if (draft === null || draft.length < PASSWORD_MIN_LENGTH) return;
    update.mutate({ password: draft });
  };

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-4">
      <div>
        <Label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Password
        </Label>
        {draft !== null ? (
          <div className="mt-2 flex items-center gap-2">
            <Input
              type="password"
              autoFocus
              autoComplete="new-password"
              aria-label="New lookbook password"
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              value={draft}
              maxLength={128}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") savePassword();
                if (e.key === "Escape") setDraft(null);
              }}
              className="h-8 flex-1 text-sm"
            />
            <Button
              type="button"
              size="sm"
              className="h-8"
              onClick={savePassword}
              disabled={update.isPending || draft.length < PASSWORD_MIN_LENGTH}
            >
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={() => setDraft(null)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <KeyRound className="h-3.5 w-3.5" />
              {lookbook.password_protected
                ? "Visitors must enter a password to view it."
                : "No password — the link alone opens it."}
            </p>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setDraft("")}
                >
                  {lookbook.password_protected ? "Change password" : "Set password"}
                </Button>
                {lookbook.password_protected && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-destructive hover:text-destructive"
                    onClick={() => update.mutate({ password: null })}
                    disabled={update.isPending}
                  >
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <Label
            htmlFor={`guest-engagement-${lookbook._id}`}
            className="text-sm font-medium"
          >
            Guest comments and upvotes
          </Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Let visitors upvote and comment without signing in. They can add a
            name to their comments, or post as “Guest”.
          </p>
        </div>
        <Switch
          id={`guest-engagement-${lookbook._id}`}
          checked={lookbook.guest_engagement}
          onCheckedChange={(checked) =>
            update.mutate({ guest_engagement: checked })
          }
          disabled={!canEdit || update.isPending}
          aria-label="Allow guest comments and upvotes"
        />
      </div>
    </div>
  );
}
